import { useAuthStore } from '../store/useAuthStore';
import type {
  CreateReportBody,
  CreateReportResponse,
} from '@shared/reports.js';
import type {
  BlockListItem,
  CreateBlockBody,
} from '@shared/blocks.js';
import type { PanicEventResponse } from '@shared/panic.js';
import type {
  AppealShowResponse,
  SubmitAppealBody,
} from '@shared/appeals.js';
import type { MyStrikesResponse } from '@shared/risk.js';

interface Envelope<T> {
  sessionState: string;
  success: boolean;
  data: T | null;
  messages: Array<{ severity: string; message: string; details?: Record<string, unknown> }>;
}

export class SafetyApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

function buildHeaders(includeCsrf: boolean = true, contentType: string | null = 'application/json'): Headers {
  const headers = new Headers();
  if (contentType !== null) headers.set('Content-Type', contentType);
  if (includeCsrf) {
    const csrf = useAuthStore.getState().csrfToken;
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  return headers;
}

async function unwrap<T>(res: Response): Promise<T> {
  const envelope = (await res.json()) as Envelope<T>;
  if (!res.ok || envelope.success === false || envelope.data === null) {
    const msg = envelope.messages?.[0];
    const code = (msg?.details?.['code'] as string | undefined) ?? 'unknown';
    throw new SafetyApiError(res.status, code, msg?.message ?? `HTTP ${res.status}`);
  }
  return envelope.data;
}

// --- Reports ---

export async function createReport(body: CreateReportBody): Promise<CreateReportResponse> {
  const res = await fetch('/api/v1/reports', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return unwrap<CreateReportResponse>(res);
}

export async function uploadReportScreenshot(reportId: string, file: Blob): Promise<{ url: string }> {
  const form = new FormData();
  form.append('screenshot', file, 'screenshot.webp');
  const res = await fetch(`/api/v1/reports/${reportId}/screenshot`, {
    method: 'POST',
    headers: buildHeaders(true, null),
    credentials: 'include',
    body: form,
  });
  return unwrap<{ url: string }>(res);
}

// --- Blocks ---

export async function createBlock(body: CreateBlockBody): Promise<{ id: number; idempotent?: boolean }> {
  const res = await fetch('/api/v1/blocks', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return unwrap<{ id: number; idempotent?: boolean }>(res);
}

export async function deleteBlock(blockedUserId: string): Promise<{ unblocked: boolean }> {
  const res = await fetch(`/api/v1/blocks/${blockedUserId}`, {
    method: 'DELETE',
    headers: buildHeaders(),
    credentials: 'include',
  });
  return unwrap<{ unblocked: boolean }>(res);
}

export async function listBlocks(): Promise<{ items: BlockListItem[] }> {
  const res = await fetch('/api/v1/blocks', {
    method: 'GET',
    headers: buildHeaders(false),
    credentials: 'include',
  });
  return unwrap<{ items: BlockListItem[] }>(res);
}

// --- Panic ---

export async function uploadPanicCaptures(
  matchId: string,
  peerUserId: string,
  captureLocal: Blob | null,
  captureRemote: Blob | null,
  optionalDetails?: string,
): Promise<PanicEventResponse> {
  const form = new FormData();
  form.append('match_id', matchId);
  form.append('peer_user_id', peerUserId);
  if (captureLocal !== null) form.append('capture_local', captureLocal, 'local.webp');
  if (captureRemote !== null) form.append('capture_remote', captureRemote, 'remote.webp');
  if (optionalDetails !== undefined && optionalDetails !== '') {
    form.append('optional_details', optionalDetails);
  }
  const res = await fetch('/api/v1/panic/captures', {
    method: 'POST',
    headers: buildHeaders(true, null),
    credentials: 'include',
    body: form,
  });
  return unwrap<PanicEventResponse>(res);
}

// --- Safety check-in ---

export async function submitSafetyCheckin(matchId: string, response: 'safe' | 'report' | 'dismissed'): Promise<{ id: string }> {
  const res = await fetch('/api/v1/safety/checkin', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ match_id: matchId, response }),
  });
  return unwrap<{ id: string }>(res);
}

// --- Me strikes ---

export async function getMyStrikes(): Promise<MyStrikesResponse> {
  const res = await fetch('/api/v1/me/strikes', {
    method: 'GET',
    headers: buildHeaders(false),
    credentials: 'include',
  });
  return unwrap<MyStrikesResponse>(res);
}

// --- Appeals (UNAUTHENTICATED) ---

export async function showAppeal(token: string): Promise<AppealShowResponse> {
  const res = await fetch(`/api/v1/appeals/${token}`, {
    method: 'GET',
    headers: new Headers(),
    credentials: 'omit',
  });
  return unwrap<AppealShowResponse>(res);
}

export async function submitAppeal(token: string, body: SubmitAppealBody): Promise<{ submitted: boolean }> {
  const res = await fetch(`/api/v1/appeals/${token}/submit`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    credentials: 'omit',
    body: JSON.stringify(body),
  });
  return unwrap<{ submitted: boolean }>(res);
}
